
import Modal from "@/components/Modal.vue";
import EventBus from "@/lib/event-bus";
import FirstStep from "@/components/PayWall/FirstStep.vue";
import SecondStep from "@/components/PayWall/SecondStep.vue";
import ThirdStep from "@/components/PayWall/ThirdStep.vue";
import SuspendedStep from "@/components/PayWall/SuspendedStep.vue";
import { apiCall } from "@/lib/api";
import Loading from "@/components/Loading.vue";
import { differenceNowDays, gettersInstance, parseDate } from "@/lib/util";
import ContentTransition from "@/components/ContentTransition.vue";
import { Component, MixedVue, mixins } from "@/vue";
import { Ref } from "vue-property-decorator";
import { LegalTerm } from "@/lib/types/LegalTerm";
import { LicenseOffer } from "@/lib/types/LicenseOffer";
import { ComputedLicencePlan } from "@/lib/types/misc/ComputedLicencePlan";
import { LicenseChooseRequest } from "@/lib/types/actions/LicenseChooseRequest";
import CardPayment from "./CardPayment.vue";
import { UserLicense } from "@/lib/types/User";

const StoreGetters = gettersInstance(["userLicense", "userLicenseIsActive", "userLicenseIsTrial", "userLicenceIsPreTrial"]);

@Component({
  name: "PayWall",
  components: {
    Loading,
    ThirdStep,
    SuspendedStep,
    SecondStep,
    FirstStep,
    CardPayment,
    Modal,
    ContentTransition,
  },
})
export default class PayWall extends mixins(MixedVue, StoreGetters) {
  static components: {
    Modal: typeof Modal;
    Loading: typeof Loading;
    FirstStep: typeof FirstStep;
    SecondStep: typeof SecondStep;
    ThirdStep: typeof ThirdStep;
    SuspendedStep: typeof SuspendedStep;
    CardPayment: typeof CardPayment;
    ContentTransition: typeof ContentTransition;
  };

  @Ref()
  modal: Modal;

  loading: boolean = false;
  dismiss: boolean = false;
  selectedPlan: ComputedLicencePlan = null;
  confirmedPlan: UserLicense = null;
  offer: LicenseOffer = null;
  latestMarketingTerms: LegalTerm = null;

  get isSuspended() {
    return this.userLicense && this.userLicense.status === "stop";
  }

  get payByCard() {
    return this.confirmedPlan && this.confirmedPlan.card_payment && this.confirmedPlan.card_payment.payment_status != "succeeded";
  }

  get modalSize() {
    if (this.isSuspended) return "auto";
    if (!this.selectedPlan || !this.confirmedPlan) return "medium";
    return "auto";
  }
  get startDate() {
    return this.userLicense && parseDate(this.userLicense.start_date);
  }
  get endDate() {
    return this.userLicense && parseDate(this.userLicense.end_date);
  }
  get daysLeft() {
    if (!this.endDate) return 0;
    return differenceNowDays(this.endDate);
  }

  created() {
    const query = this.$route.query;
    if (query["payment_intent"] && query["redirect_status"] === "succeeded") {
      this.dismiss = true;
    }
    EventBus.$on(EventBus.events.showPaywall, async () => {
      // Update license if pre-trial user has added parcels
      try {
        this.userLicenceIsPreTrial && (await this.$store.dispatch("getCurrentUser"));
      } catch (error) {
        this.notify({
          type: "error",
          text: this.$gettext("Something went wrong."),
        });
      }
      this.dismiss = false;
      this.reset();
    });
  }

  mounted() {
    this.reset();
    if (!this.userLicense) return;
    if (this.userLicense.card_payment && this.userLicense.card_payment.payment_status != "succeeded") {
      // @ts-ignore
      this.selectedPlan = this.userLicense;
      this.confirmedPlan = this.userLicense;
    }
  }

  async reset() {
    this.selectedPlan = null;
    this.confirmedPlan = null;
    this.offer = null;
    this.loadInitialData();
  }

  async loadInitialData() {
    if (!this.userLicense) return;
    if (this.isSuspended) return;
    try {
      this.offer = await apiCall<LicenseOffer>("GET", `/license/${this.userLicense.id}/offer/`);
      this.latestMarketingTerms = await apiCall("GET", `/legal/marketing/`);
    } finally {
      // pass
    }
  }

  async decideLater() {
    try {
      this.loading = true;
      await apiCall("POST", `/license/${this.userLicense.id}/later/`);
      this.modal.closeModal();
    } finally {
      this.loading = false;
    }
  }

  async confirmPlanToAPI(data: LicenseChooseRequest) {
    try {
      this.loading = true;
      this.confirmedPlan = await apiCall("POST", `/license/${this.userLicense.id}/offer/`, {}, data);
      if (data.marketing) {
        await apiCall("PATCH", "/auth/user/", {}, { marketing: true });
      }
    } finally {
      this.loading = false;
    }
  }

  async switchToPo() {
    await apiCall("PUT", `/payment/${this.confirmedPlan.id}/switch_to_po/`);
    this.confirmedPlan.card_payment = null;
  }
}
